/**
 * Utilities for Geocoding and Reverse Geocoding using the in-browser Google Maps JS SDK
 */

export interface Coordinates {
  lat: number
  lng: number
}

/** Default fallback coordinate (London, UK) */
export const DEFAULT_COORDINATES: Coordinates = {
  lat: 51.5074,
  lng: -0.1278,
}

/**
 * Geocodes an address string or Google Place ID into latitude/longitude coordinates.
 * Runs directly in the browser using window.google.maps.Geocoder to avoid CORS restrictions.
 */
export async function geocodeAddress(options: {
  address?: string
  placeId?: string
}): Promise<Coordinates | null> {
  const { address, placeId } = options
  if (!address && !placeId) return null

  if (typeof window === 'undefined' || !(window as any).google?.maps?.Geocoder) {
    return null
  }

  try {
    const geocoder = new (window as any).google.maps.Geocoder()
    const request: any = placeId ? { placeId } : { address }

    return await new Promise<Coordinates | null>((resolve) => {
      geocoder.geocode(request, (results: any, status: string) => {
        if (status === 'OK' && results && results[0]?.geometry?.location) {
          const loc = results[0].geometry.location
          const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat
          const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng
          resolve({ lat: Number(lat), lng: Number(lng) })
        } else {
          resolve(null)
        }
      })
    })
  } catch (error) {
    console.warn('Geocoding error in browser Geocoder:', error)
    return null
  }
}

/**
 * Detailed parsed address components from geocoding
 */
export interface GeocodedAddressDetails {
  formatted_address: string
  street_address: string
  city: string
  state_or_region: string
  postal_code: string
  country: string
  latitude: number
  longitude: number
}

export interface AddressComponentLike {
  types: string[]
  long_name: string
  short_name: string
}

/**
 * Parses Google Maps address components into structured fields.
 */
export function parseGoogleAddressComponents(components: AddressComponentLike[] = []) {
  let streetNumber = ''
  let route = ''
  let city = ''
  let postalCode = ''
  let countryCode = 'GB'
  let stateOrRegion = ''

  for (const component of components) {
    const types = component.types || []
    if (types.includes('street_number')) streetNumber = component.long_name
    if (types.includes('route')) route = component.long_name
    if (types.includes('locality') || types.includes('postal_town') || types.includes('sublocality_level_1')) {
      city = component.long_name
    }
    if (types.includes('postal_code')) postalCode = component.long_name
    if (types.includes('country')) countryCode = (component.short_name || 'GB').toUpperCase()
    if (types.includes('administrative_area_level_1') || types.includes('administrative_area_level_2')) {
      if (!stateOrRegion || types.includes('administrative_area_level_1')) {
        stateOrRegion = component.long_name
      }
    }
  }

  const streetAddress = [streetNumber, route].filter(Boolean).join(' ')

  return {
    street_address: streetAddress,
    city,
    postal_code: postalCode,
    country: countryCode,
    state_or_region: stateOrRegion,
  }
}

/**
 * Reverse geocodes latitude/longitude coordinates into full structured address details.
 */
export async function reverseGeocodeAddressDetails(lat: number, lng: number): Promise<GeocodedAddressDetails | null> {
  if (typeof window === 'undefined') return null

  if ((window as any).google?.maps?.Geocoder) {
    try {
      const geocoder = new (window as any).google.maps.Geocoder()
      return await new Promise<GeocodedAddressDetails | null>((resolve) => {
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
          if (status === 'OK' && results && results.length > 0) {
            const validResult = results.find((r: any) => !r.types?.includes('plus_code')) || results[0]
            const parsed = parseGoogleAddressComponents(validResult.address_components || [])
            const formatted = validResult.formatted_address || ''
            resolve({
              formatted_address: formatted,
              street_address: parsed.street_address || formatted || 'Current Location',
              city: parsed.city || 'London',
              state_or_region: parsed.state_or_region || 'London',
              postal_code: parsed.postal_code || '',
              country: parsed.country || 'GB',
              latitude: lat,
              longitude: lng,
            })
          } else {
            resolve(null)
          }
        })
      })
    } catch (e) {
      console.warn('In-browser reverse geocode address details failed:', e)
    }
  }

  return null
}

/**
 * Reverse geocodes latitude/longitude coordinates into a human-readable address.
 */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  if (typeof window === 'undefined') return null

  // 1. Try in-browser Google Maps Geocoder if loaded
  if ((window as any).google?.maps?.Geocoder) {
    try {
      const geocoder = new (window as any).google.maps.Geocoder()
      return await new Promise<string | null>((resolve) => {
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
          if (status === 'OK' && results && results.length > 0) {
            const validResult = results.find((r: any) => !r.types?.includes('plus_code')) || results[0]
            resolve(validResult.formatted_address || null)
          } else {
            resolve(null)
          }
        })
      })
    } catch (e) {
      console.warn('In-browser reverse geocode failed:', e)
    }
  }

  return null
}
